/**
 * Source diversity and citation origin grouping for Ask Lens evidence presentation.
 * Pure functions — citations and retrieval_debug only; no backend changes.
 */

import { linkCitationChunk, isIrlCitation, isObservationCitation } from './citationDisplay';
import { normalizeEvidenceSourceBucket } from './accuracyEvidenceMeta';
import { normalizeSourceMixCounts } from './accuracyModeMeta';
import { isDbToolResponse, isGroundedSynthesisResponse, normalizeExecutionPathKind } from './trustStripMeta';

export const DIVERSITY_LEVELS = {
    EXCELLENT: 'excellent',
    GOOD: 'good',
    LIMITED: 'limited',
    SINGLE: 'single',
};

export const DIVERSITY_LABELS = {
    [DIVERSITY_LEVELS.EXCELLENT]: 'Excellent diversity',
    [DIVERSITY_LEVELS.GOOD]: 'Good diversity',
    [DIVERSITY_LEVELS.LIMITED]: 'Limited diversity',
    [DIVERSITY_LEVELS.SINGLE]: 'Single source',
};

export const SOURCE_LEGEND = [
    { key: 'database', label: 'Live Database', color: '#22c55e' },
    { key: 'documents', label: 'Documents', color: '#5a6fd4' },
    { key: 'irls', label: 'IRLs', color: '#3d9a6e' },
    { key: 'observations', label: 'Observations', color: '#2563eb' },
    { key: 'synthesis', label: 'AI synthesis', color: '#989bad' },
];

function safeRetrievalDebug(retrievalDebug) {
    return retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
}

function isVdrCitation(citation, linkedChunk) {
    if (isObservationCitation(citation, linkedChunk) || isIrlCitation(citation, linkedChunk)) {
        return false;
    }
    const st = String(citation?.source_type || linkedChunk?.source_type || '')
        .toLowerCase()
        .trim();
    if (st === 'vdr_chunk' || st === 'vdr') return true;
    return Boolean(citation?.file_id != null || citation?.chunk_id != null || linkedChunk?.file_id != null);
}

export function isDatabaseCitation(citation, linkedChunk) {
    if (isVdrCitation(citation, linkedChunk)) return false;
    if (isIrlCitation(citation, linkedChunk) || isObservationCitation(citation, linkedChunk)) {
        return false;
    }
    const st = String(citation?.source_type || linkedChunk?.source_type || '')
        .toLowerCase()
        .trim();
    if (
        st.includes('db') ||
        st.includes('canonical') ||
        st.includes('task') ||
        st.includes('raid') ||
        st.includes('record')
    ) {
        return true;
    }
    return Boolean(
        citation?.source_id != null &&
            citation?.file_id == null &&
            citation?.irl_id == null &&
            citation?.observation_id == null,
    );
}

export function groupCitationOrigins(citations = [], chunks = [], retrievalDebug = {}) {
    const files = new Set();
    const workstreams = new Set();
    const folders = new Set();
    const irls = new Set();
    const observations = new Set();
    const databaseEntities = new Set();
    const seenRefs = new Set();

    for (const citation of Array.isArray(citations) ? citations : []) {
        const ref = String(citation?.ref ?? '').trim();
        if (ref && seenRefs.has(ref)) continue;
        if (ref) seenRefs.add(ref);

        const linked = linkCitationChunk(citation, chunks);
        const fileId = citation?.file_id ?? linked?.file_id;
        const docName =
            citation?.document_name ||
            citation?.metadata_storage_name ||
            linked?.document_name ||
            linked?.metadata_storage_name;
        if (fileId != null) files.add(`file:${fileId}`);
        else if (docName) files.add(`doc:${String(docName).trim().toLowerCase()}`);

        const ws =
            citation?.workstream_name ||
            citation?.workstream_repr ||
            linked?.workstream_name ||
            linked?.workstream_repr;
        if (ws) workstreams.add(String(ws).trim());

        const folderId =
            citation?.folder_id ??
            linked?.folder_id ??
            linked?.metadata?.folder_id ??
            citation?.metadata?.folder_id;
        if (folderId != null) folders.add(`folder:${folderId}`);

        if (isIrlCitation(citation, linked)) {
            const irlKey =
                citation?.irl_id ??
                citation?.irl_ref ??
                linked?.irl_id ??
                linked?.irl_ref ??
                citation?.ref;
            if (irlKey != null) irls.add(String(irlKey));
        }

        if (isObservationCitation(citation, linked)) {
            const obsKey =
                citation?.observation_id ??
                citation?.observation_ref ??
                linked?.observation_id ??
                linked?.observation_ref ??
                citation?.ref;
            if (obsKey != null) observations.add(String(obsKey));
        }

        if (isDatabaseCitation(citation, linked)) {
            const dbKey =
                citation?.source_id ??
                citation?.document_name ??
                citation?.metadata_storage_name ??
                citation?.ref;
            if (dbKey != null) databaseEntities.add(String(dbKey));
        }
    }

    const rd = safeRetrievalDebug(retrievalDebug);
    if (databaseEntities.size === 0 && isDbToolResponse(rd)) {
        const provenance = Array.isArray(rd.answer_provenance) ? rd.answer_provenance : [];
        provenance.filter(Boolean).forEach((entry, index) => {
            databaseEntities.add(String(entry || `record-${index}`));
        });
    }

    const mix = normalizeSourceMixCounts(rd.source_mix ?? rd.coverage_report?.source_mix);

    return {
        files,
        workstreams,
        folders,
        irls,
        observations,
        databaseEntities,
        counts: {
            files: files.size,
            workstreams: workstreams.size,
            folders: folders.size,
            irls: irls.size,
            observations: observations.size,
            databaseEntities: databaseEntities.size,
        },
        mix,
        retrievalDebug: rd,
    };
}

/**
 * Deterministic diversity scoring from unique origin counts.
 *
 * | Level     | Rule |
 * |-----------|------|
 * | Single    | Total unique origins ≤ 1 |
 * | Limited   | Total unique origins = 2 OR only one origin type represented |
 * | Good      | 3–5 unique origins OR two origin types with ≥ 3 combined |
 * | Excellent | ≥ 6 unique origins OR ≥ 3 origin types |
 */
export function computeSourceDiversity(origins) {
    const counts = origins?.counts || {};
    const activeTypes = [
        counts.databaseEntities > 0,
        counts.files > 0,
        counts.irls > 0,
        counts.observations > 0,
    ].filter(Boolean).length;

    const totalUnique =
        (counts.databaseEntities || 0) +
        (counts.files || 0) +
        (counts.irls || 0) +
        (counts.observations || 0);

    let level = DIVERSITY_LEVELS.GOOD;
    if (totalUnique <= 1) level = DIVERSITY_LEVELS.SINGLE;
    else if (totalUnique === 2 || activeTypes <= 1) level = DIVERSITY_LEVELS.LIMITED;
    else if (totalUnique >= 6 || activeTypes >= 3) level = DIVERSITY_LEVELS.EXCELLENT;
    else if (totalUnique >= 3 && totalUnique <= 5) level = DIVERSITY_LEVELS.GOOD;

    return {
        level,
        label: DIVERSITY_LABELS[level],
        totalUnique,
        activeTypes,
        counts,
    };
}

export function inferConsistencyLabel(origins) {
    const counts = origins?.counts || {};
    const docLike = counts.files || 0;
    const activeTypes = [
        counts.databaseEntities > 0,
        counts.files > 0,
        counts.irls > 0,
        counts.observations > 0,
    ].filter(Boolean).length;

    if (docLike === 1 && activeTypes === 1 && counts.databaseEntities === 0) {
        return {
            key: 'single-document',
            label: 'Single-document answer',
            tone: 'warning',
        };
    }

    const total =
        (counts.databaseEntities || 0) +
        (counts.files || 0) +
        (counts.irls || 0) +
        (counts.observations || 0);

    if (total >= 3 || activeTypes >= 2) {
        return {
            key: 'multi-source',
            label: 'Multi-source verified',
            tone: 'success',
        };
    }

    return null;
}

export function summarizeEvidence({
    citations = [],
    chunks = [],
    retrievalDebug = {},
    confidence = '',
    evidenceGovernance = null,
    trustStripLabels = [],
}) {
    const origins = groupCitationOrigins(citations, chunks, retrievalDebug);
    const diversity = computeSourceDiversity(origins);
    const consistency = inferConsistencyLabel(origins);
    const rd = origins.retrievalDebug;
    const mix = origins.mix;

    const vdrCount = Math.max(origins.counts.files, mix.proof);
    const irlCount = Math.max(origins.counts.irls, mix.management);
    const obsCount = Math.max(origins.counts.observations, mix.consultant);
    let dbCount = origins.counts.databaseEntities;
    if (!dbCount && isDbToolResponse(rd)) {
        dbCount = Math.max(
            Number(rd.fused_count) || 0,
            Array.isArray(rd.answer_provenance) ? rd.answer_provenance.length : 0,
        );
    }

    const workstreamNames = [...origins.workstreams].slice(0, 6);
    const confidenceLabel = formatConfidenceLabel(confidence, rd);
    const grounded = resolveGroundedLabel(rd, evidenceGovernance);

    const rows = [];
    if (dbCount > 0) rows.push({ key: 'database', label: 'Live Database', count: dbCount, unit: dbCount === 1 ? 'record' : 'records' });
    if (vdrCount > 0) rows.push({ key: 'documents', label: 'VDR Documents', count: vdrCount, unit: vdrCount === 1 ? 'citation' : 'citations' });
    if (irlCount > 0) rows.push({ key: 'irls', label: 'IRL Documents', count: irlCount, unit: irlCount === 1 ? 'reference' : 'references' });
    if (obsCount > 0) rows.push({ key: 'observations', label: 'Observations', count: obsCount, unit: obsCount === 1 ? 'finding' : 'findings' });

    const qualityIndicators = buildQualityIndicators({
        retrievalDebug: rd,
        diversity,
        consistency,
        trustStripLabels,
    });

    return {
        rows,
        workstreams: workstreamNames,
        confidenceLabel,
        grounded,
        diversity,
        consistency,
        qualityIndicators,
        legend: SOURCE_LEGEND,
        origins,
        hasContent: rows.length > 0 || workstreamNames.length > 0 || Boolean(confidenceLabel),
    };
}

function formatConfidenceLabel(confidence, retrievalDebug) {
    const raw = confidence || retrievalDebug?.decomposed_confidence?.band || retrievalDebug?.coverage_report?.decomposed_confidence?.band;
    if (!raw) return null;
    const value = String(raw).trim();
    if (!value) return null;
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function resolveGroundedLabel(retrievalDebug, evidenceGovernance) {
    const gov = evidenceGovernance && typeof evidenceGovernance === 'object' ? evidenceGovernance : {};
    if (gov.grounded === true || gov.is_grounded === true) return 'Yes';
    if (gov.grounded === false || gov.is_grounded === false) return 'No';
    if (isGroundedSynthesisResponse(retrievalDebug)) return 'Yes';
    const pathKind = normalizeExecutionPathKind(retrievalDebug);
    if (pathKind === 'grounded_rag' || pathKind === 'planner' || pathKind === 'hybrid') return 'Yes';
    if (pathKind === 'legacy_rag') return 'Partial';
    return null;
}

function buildQualityIndicators({ retrievalDebug, diversity, consistency, trustStripLabels }) {
    const trust = new Set((trustStripLabels || []).map(label => String(label).toLowerCase()));
    const indicators = [];
    const rd = safeRetrievalDebug(retrievalDebug);
    const pathKind = normalizeExecutionPathKind(rd);

    const groundedLabel = resolveGroundedLabel(rd, null);
    if (groundedLabel === 'Yes' && !trust.has('verified evidence')) {
        indicators.push({ key: 'grounded', label: 'Grounded', tone: 'success' });
    }

    if (consistency?.key === 'multi-source') {
        indicators.push({ key: 'multi-source', label: 'Multi-source', tone: 'success' });
    }

    const historyTurns = Number(rd.conversation_history_turns);
    if (Number.isFinite(historyTurns) && historyTurns > 0 && !trust.has('conversation context')) {
        indicators.push({ key: 'conversation-aware', label: 'Conversation aware', tone: 'neutral' });
    }

    if ((pathKind === 'planner' || pathKind === 'hybrid') && !trust.has('mixed sources')) {
        indicators.push({ key: 'planner-assisted', label: 'Planner assisted', tone: 'neutral' });
    }

    if (pathKind === 'database' && !trust.has('live database')) {
        indicators.push({ key: 'database-verified', label: 'Database verified', tone: 'success' });
    }

    if (diversity?.level === DIVERSITY_LEVELS.EXCELLENT || diversity?.level === DIVERSITY_LEVELS.GOOD) {
        indicators.push({ key: 'diversity', label: diversity.label, tone: 'success' });
    }

    return indicators;
}
